/**
 * Back Button Component
 *
 * Reusable back button component that navigates to the previous page.
 * Provides consistent styling and behavior across the application.
 *
 * @module components/ui/BackButton
 */

import React from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@/components/ui/Button';

interface BackButtonProps {
  /** Custom text for the button (default: "Back") */
  text?: string;
  /** Button variant */
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  /** Button size */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Additional CSS classes */
  className?: string;
  /** Fallback route to navigate to when no history exists (default: "/") */
  fallback?: string;
  /** Whether the button is disabled */
  disabled?: boolean;
}

/**
 * Back Button Component
 */
export const BackButton: React.FC<BackButtonProps> = ({
  text = 'Back',
  variant = 'outline',
  size = 'sm',
  className = '',
  fallback = '/',
  disabled = false,
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    // Only navigate back if there are pages in the history stack
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // If no history, navigate to fallback route
      navigate(fallback);
    }
  };

  return (
    <Button
      onClick={handleClick}
      variant={variant}
      size={size}
      disabled={disabled}
      className={`inline-flex items-center ${className}`}
    >
      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
      {text}
    </Button>
  );
};
